import { Form } from 'antd';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { Store } from '../../../state/store.interfaces';
import Icon from '../../../common/components/Icon/Icon';
import { LrvForm } from '../../../common/components/LrvForm/LrvForm';
import { LrvText } from '../../../common/components/LrvText/LrvText';
import { useFormErrorFields } from '../../../hooks/useFormErrorFields';
import { LrvModal } from '../../../common/components/LrvModal/LrvModal';
import { LrvInputV2 } from '../../../common/components/LrvInputV2/LrvInputV2';

import * as mfaSlice from './mfaSlice';
import styles from './AuthenticatorAppForm.module.scss';

interface Props {
  theme?: 'dark' | 'light';
}

export const AuthenticatorAppForm = (props: Props) => {
  const { theme } = props;

  const [t] = useTranslation();
  const [form] = Form.useForm();
  const { errorFields, resetErrorFields, extractAndSetErrorFields } = useFormErrorFields({ form });

  const dispatch = useDispatch();

  const [code, setCode] = useState('');

  const {
    showCreateAuthenticatorAppModal,
    qrCode,
    isUpdating,
  } = useSelector((state: Store) => state.mfa);

  const onCloseModal = () => {
    dispatch(mfaSlice.setQrCode(''));
    dispatch(mfaSlice.setShowCreateAuthenticatorAppModal(false));

    form.resetFields();
    resetErrorFields();
  };

  const renderQrCode = () => {
    if (!qrCode) {
      return null;
    }

    const hasError = errorFields.includes('token');
    return (
      <>
        <Form.Item
          label={t('mfa.authenticatorApp.download.title')}
          className={styles.formItem}
        >
          <LrvText text={t('mfa.authenticatorApp.download.description')} theme={theme} />
        </Form.Item>

        <Form.Item
          label={t('mfa.authenticatorApp.obtainCode.title')}
          className={styles.formItem}
        >
          <LrvText text={t('mfa.authenticatorApp.obtainCode.description')} theme={theme} />
        </Form.Item>

        <Form.Item name='codeqr'>
          <div className={styles.qrCode}>
            <img src={qrCode} alt='Google Authenticator' />
          </div>
        </Form.Item>

        <Form.Item
          name='token'
          rules={[{ required: true, message: t('common.requiredField') }]}
          label={t('mfa.authenticatorApp.enterCode')}
          className={styles.formItem}
        >
          <LrvInputV2
            size='large'
            theme={theme}
            onChange={(e) => setCode(e.target.value)}
            label={t('mfa.codeMFA')}
            hasError={hasError}
          />
        </Form.Item>
      </>
    );
  };

  return (
    <LrvModal
      theme={theme}
      title={t('mfa.title')}
      open={showCreateAuthenticatorAppModal}
      destroyOnClose={true}
      className={styles.container}
      closeIcon={<Icon id='close_new_user_modal' name='close' />}
      cancelText={t('actions.cancel')}
      onCancel={onCloseModal}
      okText={t('actions.save')}
      okButtonProps={{ loading: isUpdating }}
      onOk={() => {
        dispatch(mfaSlice.updateAuthenticatorApp({ token: code, onSuccess: onCloseModal }));
      }}
    >
      <LrvForm
        id='form'
        form={form}
        name='form'
        layout='vertical'
        theme={theme}
        onFieldsChange={extractAndSetErrorFields}
      >
        {renderQrCode()}
      </LrvForm>
    </LrvModal>
  );
};
